import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import {
  RealtimeMessage,
  RealtimeEventName,
  SocketSubscribeFilters,
  createRealtimeSocket
} from '@/lib/socket';
import { useAuth } from '@/context/AuthContext';
import { useToasts } from '@/context/ToastContext';
import { tokenStore } from '@/lib/tokenStore';

export type RealtimeStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'disconnected';

interface RealtimeContextValue {
  status: RealtimeStatus;
  lastEvent: RealtimeMessage | null;
  subscribe: (filters: SocketSubscribeFilters) => void;
  on: (event: RealtimeEventName, handler: (data: Record<string, unknown>) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextValue | undefined>(undefined);

export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const [status, setStatus] = useState<RealtimeStatus>('idle');
  const [lastEvent, setLastEvent] = useState<RealtimeMessage | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const listenersRef = useRef<Map<RealtimeEventName, Set<(data: Record<string, unknown>) => void>>>(
    new Map()
  );

  useEffect(() => {
    if (!isAuthenticated || !user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setStatus('idle');
      return;
    }

    if (!tokenStore.getAccessToken()) {
      setStatus('idle');
      return;
    }

    setStatus('connecting');
    const wsUrl = (import.meta.env.VITE_WS_URL ?? import.meta.env.VITE_API_URL ?? '').trim();
    const socket = createRealtimeSocket({
      // Pass a getter so each reconnect attempt picks up the freshest access
      // token (after silent refresh by the axios interceptor). Avoids the
      // infinite-reconnect / fake auto-logout loop with stale JWTs.
      getToken: () => tokenStore.getAccessToken(),
      wsUrl,
      filters: { event_types: undefined }
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setStatus('connected');
    });
    socket.on('disconnect', () => {
      setStatus('disconnected');
    });
    socket.on('connect_error', () => {
      setStatus('error');
    });
    socket.on('message', (raw: unknown) => {
      if (!raw || typeof raw !== 'object') return;
      const payload = raw as Partial<RealtimeMessage>;
      if (typeof payload.event !== 'string') return;
      const message: RealtimeMessage = {
        event: payload.event as RealtimeEventName,
        data: (payload.data ?? {}) as Record<string, unknown>
      };
      setLastEvent(message);

      const handlers = listenersRef.current.get(message.event);
      if (handlers) {
        handlers.forEach((fn) => {
          try {
            fn(message.data);
          } catch {
            // ignore listener errors
          }
        });
      }

      // Generic cache invalidation strategy.
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['tickets'] });

      switch (message.event) {
        case 'ticket.created':
          push({ kind: 'info', title: 'New ticket created', message: formatTicketInfo(message.data) });
          break;
        case 'ticket.status_changed':
          push({
            kind: 'info',
            title: 'Ticket status changed',
            message: formatStatusChange(message.data)
          });
          break;
        case 'ticket.approval_pending':
          push({
            kind: 'warning',
            title: 'Approval required',
            message: `Ticket ${extractTicketId(message.data) ?? ''} is waiting for Admin approval`
          });
          break;
        case 'ticket.approval_decided':
          push({
            kind: 'success',
            title: 'Approval updated',
            message: `Ticket ${extractTicketId(message.data) ?? ''} decided: ${
              (message.data.decision as string) ?? ''
            }`
          });
          break;
        case 'ticket.cx_informed':
          // lightweight, no toast spam
          break;
        case 'ticket.updated':
          // usually chatty; no toast
          break;
        case 'shopify.webhook_received':
          push({
            kind: 'info',
            title: 'Shopify webhook',
            message: String(message.data.topic ?? '')
          });
          break;
        default:
          break;
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setStatus('idle');
    };
  }, [isAuthenticated, user, queryClient, push]);

  const subscribe = React.useCallback((filters: SocketSubscribeFilters) => {
    socketRef.current?.emit('subscribe', { filters });
  }, []);

  const on = React.useCallback(
    (event: RealtimeEventName, handler: (data: Record<string, unknown>) => void) => {
      let set = listenersRef.current.get(event);
      if (!set) {
        set = new Set();
        listenersRef.current.set(event, set);
      }
      set.add(handler);
      return () => {
        set?.delete(handler);
      };
    },
    []
  );

  const value = useMemo<RealtimeContextValue>(
    () => ({ status, lastEvent, subscribe, on }),
    [status, lastEvent, subscribe, on]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return ctx;
}

function extractTicketId(data: Record<string, unknown>): string | null {
  if (typeof data.ticket_id === 'string') return data.ticket_id;
  const ticket = data.ticket as { id?: unknown } | undefined;
  if (ticket && typeof ticket.id === 'string') return ticket.id;
  return null;
}

function formatTicketInfo(data: Record<string, unknown>): string {
  const ticket = data.ticket as { id?: string; summary?: string; severity?: string } | undefined;
  if (!ticket) return '';
  return `${ticket.id ?? ''} · ${ticket.severity ?? ''} · ${ticket.summary ?? ''}`;
}

function formatStatusChange(data: Record<string, unknown>): string {
  return `${String(data.ticket_id ?? '')}: ${String(data.from ?? '')} → ${String(data.to ?? '')}`;
}
